import React, { useState, useMemo, useEffect } from 'react';
import {
    Box, Typography, Button, Chip, Tooltip,
    TextField, FormControlLabel, Checkbox, Divider, Snackbar, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
    AddCircleOutline as AddRowIcon,
    Save as SaveIcon,
    Send as SendIcon,
    CalendarToday as CalendarIcon,
    AttachFile as AttachIcon,
    BeachAccess as HolidayIcon,
    EventBusy as LeaveIcon,
    EventNote as ActivityIcon,
    Person as PersonIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import EntryRow, { DAY_COLORS } from './timesheetEntryRow';
import { API_ENDPOINTS } from '../../../services/endpoints';
import { putData, postData, getData } from '../../../services/axiosInstance';
import { getUserData, getUserRole } from '../../../utils/auth';
import Loader from '../../../common/spinner';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const isWorkingRow = (r) => r.dayType === 'Working' || (r.dayType === 'Leave' && r.leaveType === 'Work From Home');

function toLocalDateStr(d) {
    const y  = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${mo}-${dd}`;
}
function getMondayOfCurrentWeek() {
    const t = new Date();
    const d = t.getDay() === 0 ? -6 : 1 - t.getDay();
    const m = new Date(t);
    m.setDate(t.getDate() + d);
    return m.toISOString().split('T')[0];
}
function getMondayOfWeek(dateStr) {
    if (!dateStr) return '';
    const t = new Date(dateStr + 'T00:00:00');
    const d = t.getDay() === 0 ? -6 : 1 - t.getDay();
    t.setDate(t.getDate() + d);
    return toLocalDateStr(t);
}
function getWeekEnd(s) {
    if (!s) return '';
    const d = new Date(s + 'T00:00:00'); d.setDate(d.getDate() + 6);
    return toLocalDateStr(d);
}
function getEarliestSubmitDate(s) {
    if (!s) return '';
    const d = new Date(s + 'T00:00:00'); d.setDate(d.getDate() + 4);
    return toLocalDateStr(d);
}
function getDayName(str) {
    if (!str) return '';
    return DAY_NAMES[new Date(str + 'T00:00:00').getDay()];
}
function fmtDate(str) {
    if (!str) return '';
    return new Date(str + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}
function makeEmptyRow(id, date = '', day = '', firstRowDayType, firstRowLeaveType) {
    const isWFH = firstRowDayType === 'Leave' && firstRowLeaveType === 'Work From Home';
    return {
        id,
        date,
        day,
        dayType:   isWFH ? 'Leave'            : 'Working',
        leaveType: isWFH ? 'Work From Home'   : null,
        project: null,
        project_id: 0,
        task: null,
        task_id: 0,
        description: '',
        hours: '',
        billable: true,
        location: 'Office',
        overtime: '',
        remarks: '',
        attachments: [],
        l2_status: 'NOT_REQUIRED',
        l2_comments: null,
        l2ApproverName: null,
    };
}

const WEEK_CONFLICT_MSG = {
    DRAFT:           { msg: 'A draft already exists for this week — please edit it instead.',        severity: 'warning' },
    REJECTED:        { msg: "This week's timesheet was rejected — please edit it to resubmit.",      severity: 'warning' },
    SUBMITTED:       { msg: 'A timesheet for this week is already submitted and awaiting approval.',  severity: 'error'   },
    APPROVED:        { msg: 'A timesheet for this week has already been fully approved.',             severity: 'error'   },
};

const STATUS_CFG = {
    DRAFT:           { label: 'Draft',                      bg: '#f1f5f9', color: '#64748b' },
    SUBMITTED:       { label: 'Submitted',                  bg: '#e3f2fd', color: '#1976d2' },
    L1_APPROVED:     { label: 'Manager Approved',           bg: '#ecfdf5', color: '#059669' },
    APPROVED:        { label: 'Approved',                   bg: '#E0F2F1', color: '#00796B' },
    REJECTED:        { label: 'Rejected',  bg: '#FFEBEE', color: '#D32F2F' },
};
function getStatusCfg(raw) {
    if (!raw) return null;
    const key = raw.toUpperCase().replace(/ /g, '_');
    return STATUS_CFG[key] || { label: raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase().replace(/_/g, ' '), bg: '#f1f5f9', color: '#64748b' };
}
function formatStatusDisplay(raw) {
    return getStatusCfg(raw)?.label || null;
}

// ─── Main form ────────────────────────────────────────────────────────────────
function TimesheetForm({ editData, onDone }) {
    const [weekStart, setWeekStart]               = useState(getMondayOfCurrentWeek());
    const [rows, setRows]                         = useState([makeEmptyRow(Date.now())]);
    const [employeeComment, setEmployeeComment]   = useState('');
    const [declared, setDeclared]                 = useState(false);
    const [projects, setProjects]                 = useState([]);
    const [leaveTypes, setLeaveTypes]             = useState([]);
    const [errors, setErrors]                     = useState({});
    const [snack, setSnack]                       = useState({ open: false, msg: '', severity: 'success' });
    const [existingWeeks, setExistingWeeks]       = useState({});
    const [weekCheckLoading, setWeekCheckLoading]     = useState(false);
    const [weekConflict, setWeekConflict]             = useState(null);
    const [selectedWeekStatus, setSelectedWeekStatus] = useState(null);
    const [submitDialogOpen, setSubmitDialogOpen]         = useState(false);
    const [reportingManagerName, setReportingManagerName] = useState(null);
    const [isLoading, setIsLoading] = useState(false)
    const [globalConfig, setGlobalConfig] = useState(null);
    const maxDailyHours = Number(globalConfig?.max_daily_hours ?? 8);

    const weekEnd            = getWeekEnd(weekStart);
    const earliestSubmitDate = getEarliestSubmitDate(weekStart);
    const canSubmitNow       = toLocalDateStr(new Date()) >= earliestSubmitDate;
    const userData = getUserData();
    const userId   = userData?.id;
    const orgId    = userData.organization_id;

    useEffect(() => {
        getData(API_ENDPOINTS.getTimesheetConfig())
            .then(res => setGlobalConfig(res?.result?.data ?? res?.result ?? null))
            .catch(() => {});
    }, []);

    // Fetch all existing timesheets on mount so we can validate the week picker instantly
    useEffect(() => {
        if (editData) return;
        (async () => {
            setWeekCheckLoading(true);
            try {
                const ud     = getUserData();
                const uid    = ud?.id;
                if (!uid) return;

                const countRes = await getData(API_ENDPOINTS.getAllTimesheets('', '', '', '', uid, 1, 1));
                if (countRes?.status !== 'SUCCESS') return;
                const total = countRes.result?.total || 0;
                if (total === 0) return;

                const res  = await getData(API_ENDPOINTS.getAllTimesheets('', '', '', '', uid, 1, total));
                if (res?.status !== 'SUCCESS') return;
                const list = res.result?.data ?? [];
                const map  = {};
                list.forEach(ts => {
                    const monday = getMondayOfWeek(ts.week_start_date);
                    if (monday) map[monday] = ts.rawStatus || ts.status;
                });
                setExistingWeeks(map);
                const currentMonday  = getMondayOfWeek(weekStart);
                const existingStatus = map[currentMonday];
                if (existingStatus) {
                    const key = existingStatus.toUpperCase().replace(/ /g, '_');
                    setWeekConflict(WEEK_CONFLICT_MSG[key] || { msg: 'A timesheet already exists for this week.', severity: 'error' });
                    setSelectedWeekStatus(existingStatus);
                }
            } catch {
                // Fail open — backend will catch duplicates at save time
            } finally {
                setWeekCheckLoading(false);
            }
        })();
    }, [editData]);

    const handleWeekChange = (dateStr) => {
        const monday = getMondayOfWeek(dateStr);
        setWeekStart(monday || dateStr);
        setRows([makeEmptyRow(Date.now())]);
        setEmployeeComment('');
        setDeclared(false);
        setErrors({});
        if (!monday || editData) { setWeekConflict(null); setSelectedWeekStatus(null); return; }
        const existingStatus = existingWeeks[monday];
        if (existingStatus) {
            const key = existingStatus.toUpperCase().replace(/ /g, '_');
            setWeekConflict(WEEK_CONFLICT_MSG[key] || { msg: 'A timesheet already exists for this week.', severity: 'error' });
            setSelectedWeekStatus(existingStatus);
        } else {
            setWeekConflict(null);
            setSelectedWeekStatus(null);
        }
    };

    useEffect(() => {
        const fetchProjects = async () => {
            let projectsList = [];
            let tasksList    = [];
            let leaveArray   = [];
            try {
                setIsLoading(true);
                const leaveData = await getData(API_ENDPOINTS.fetchLeaveCountsForOrganization(orgId));
                if (leaveData.status === 'SUCCESS') {
                    leaveArray = leaveData.message.leaveTypes.map(el => el.leave_type);
                    setLeaveTypes(leaveArray);
                }

                const role     = (getUserRole() || '').toLowerCase().trim();
                const isSupervisor = role === 'team lead' || role === 'manager';
                const baseUrl  = isSupervisor
                    ? API_ENDPOINTS.fetchSupervisorProjects
                    : API_ENDPOINTS.fetchEmployeeProjects;
                const url      = `${baseUrl}/${userId}`;
                const response = await getData(url);
                if (response?.status === 'SUCCESS') {
                    projectsList = response.message?.data || [];
                    setProjects(projectsList);
                    setReportingManagerName(response.message?.reporting_manager_name || null);
                }

                if (editData) {
                    const uniqueProjectIds = [...new Set(editData.entries.map(e => e.project_id).filter(Boolean))];
                    const taskFetches      = await Promise.all(
                        uniqueProjectIds.map(pid =>
                            getData(API_ENDPOINTS.fetchAllTasks({ project_id: pid, user_id: userId, limit: 100, include_subtasks: true }))
                        )
                    );
                    tasksList = taskFetches.flatMap(r => r?.status === 'SUCCESS' ? r.result?.tasks || [] : []);
                }
            } catch (e) {
                console.error(e);
            }
            finally{
                setIsLoading(false)
            }

            if (!editData) return;
            setWeekStart(editData.week_start_date);
            setRows(
                editData?.entries?.sort((a,b) => new Date(a.entry_date) - new Date(b.entry_date))?.map((e, i) => ({
                    id:          i,
                    billable:    e.is_billable ?? true,
                    date:        e.entry_date || '',
                    dayType:     e.day_type || 'Working',
                    description: e.work_description || '',
                    hours:       e.hours_worked || null,
                    leaveType:   e.day_type === 'Leave' ? (e.leave_type || leaveArray[0]) : null,
                    location:    e.day_type === 'Working' ? e.location || 'Office' : null,
                    project:     projectsList.find(p => p?.id === e.project_id)?.project_title || null,
                    project_id:  e.project_id || null,
                    task:        tasksList.find(t => t?.id === e.task_id)?.task_title || null,
                    task_id:     e.task_id || null,
                    remarks:     e.remarks || '',
                    overtime:    e.overtime_hours ?? '',
                    attachments: e.attachment_urls || [],
                    l2_status:     e.l2_status || 'NOT_REQUIRED',
                    l2_comments:   e.l2_comments || null,
                    l2ApproverName: e.l2_approver_name || null,
                }))
            );
            setEmployeeComment(editData.employee_notes || '');
            setDeclared(false);
        };
        fetchProjects();
    }, []);

    const l2RejectedRows = useMemo(() => rows.filter(r => r.l2_status === 'REJECTED'), [rows]);

    const summary = useMemo(() => {
        const w     = rows.filter(r => isWorkingRow(r) || r.dayType === 'Activity');
        const total = w.reduce((s, r) => s + Number(r.hours    || 0), 0);
        const ot    = w.reduce((s, r) => s + Number(r.overtime || 0), 0);
        return {
            total, ot,
            leaves:     rows.filter(r => r.dayType === 'Leave').length,
            holidays:   rows.filter(r => r.dayType === 'Holiday').length,
            activities: rows.filter(r => r.dayType === 'Activity').length,
            weekends:   rows.filter(r => r.dayType === 'Weekend').length,
        };
    }, [rows]);

    const hasWorkingRows = rows.some(r => isWorkingRow(r));

    const allWeekDatesFilled = useMemo(() => {
        if (!weekStart) return false;
        const enteredDates = new Set(rows.map(r => r.date).filter(Boolean));
        const start = new Date(weekStart + 'T00:00:00');
        for (let i = 0; i < 7; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            if (!enteredDates.has(toLocalDateStr(d))) return false;
        }
        return true;
    }, [rows, weekStart]);

    const updateRow = (id, field, value) =>
        setRows(prev => prev.map(r => {
            if (r.id !== id) return r;
            const u = { ...r, [field]: value };
            if (field === 'date') u.day = getDayName(value);
            return u;
        }));

    const replaceRow = (id, updater) =>
        setRows(prev => prev.map(r => r.id !== id ? r : updater(r)));

    const addRow        = ()                                          => setRows(p => [...p, makeEmptyRow(Date.now() + Math.random())]);
    const addRowForDate = (date, day, firstRowDayType, firstRowLeaveType) => setRows(p => [...p, makeEmptyRow(Date.now() + Math.random(), date, day, firstRowDayType, firstRowLeaveType)]);
    const deleteRow     = (id)                                        => setRows(p => p.filter(r => r.id !== id));

    const groupedRows = useMemo(() => {
        const groups = [];
        const seen   = {};
        rows.forEach((row, idx) => {
            if (row.date) {
                if (!seen[row.date]) {
                    seen[row.date] = { date: row.date, day: row.day, rows: [] };
                    groups.push(seen[row.date]);
                }
                seen[row.date].rows.push({ row, idx });
            } else {
                groups.push({ date: null, rows: [{ row, idx }] });
            }
        });
        return groups;
    }, [rows]);

    const validate = (forSubmit = false) => {
        const errs = {};
        // Pre-compute daily totals of regular hours (Working + Activity) for each date
        const dailyRegularTotals = {};
        rows.forEach(r => {
            if ((isWorkingRow(r) || r.dayType === 'Activity') && r.date) {
                dailyRegularTotals[r.date] = (dailyRegularTotals[r.date] || 0) + Number(r.hours || 0);
            }
        });

        rows.forEach(r => {
            if (isWorkingRow(r)) {
                if (forSubmit) {
                    if (!r.date)                          errs[`${r.id}_date`]    = 'Required';
                    if (!r.project_id)                    errs[`${r.id}_project`] = 'Required';
                    if (!r.task_id)                       errs[`${r.id}_task`]    = 'Required';
                    if ((!r.hours || Number(r.hours) <= 0) && (!r.overtime || Number(r.overtime <= 0))) errs[`${r.id}_hours`]  = 'Must be > 0';
                }
                if (r.hours && Number(r.hours) > maxDailyHours)
                    errs[`${r.id}_hours`] = `Max ${maxDailyHours} hrs`;
                if (r.date && dailyRegularTotals[r.date] > maxDailyHours)
                    errs[`${r.id}_hours`] = `Daily total exceeds ${maxDailyHours} hrs`;
            } else if (r.dayType === 'Activity') {
                if (forSubmit) {
                    if (!r.date)                          errs[`${r.id}_date`]  = 'Required';
                    if ((!r.hours || Number(r.hours) <= 0) && (!r.overtime || Number(r.overtime <= 0))) errs[`${r.id}_hours`] = 'Must be > 0';
                }
                if (r.hours && Number(r.hours) > maxDailyHours)
                    errs[`${r.id}_hours`] = `Max ${maxDailyHours} hrs`;
                if (r.date && dailyRegularTotals[r.date] > maxDailyHours)
                    errs[`${r.id}_hours`] = `Daily total exceeds ${maxDailyHours} hrs`;
            }
        });
        const seen = new Set();
        rows.filter(r => isWorkingRow(r)).forEach(r => {
            const k = `${r.date}_${r.project_id}_${r.task_id}`;
            if (r.date && r.project_id && r.task_id) { if (seen.has(k)) errs[`${r.id}_task`] = 'Duplicate entry'; seen.add(k); }
        });

        if (forSubmit && weekStart) {
            const enteredDates = new Set(rows.map(r => r.date).filter(Boolean));
            const missingDates = [];
            const start = new Date(weekStart + 'T00:00:00');
            for (let i = 0; i < 7; i++) {
                const d = new Date(start);
                d.setDate(start.getDate() + i);
                const dateStr = toLocalDateStr(d);
                if (!enteredDates.has(dateStr)) {
                    missingDates.push(dateStr);
                }
            }
            if (missingDates.length > 0) {
                errs['dates'] = `Timesheet must cover all 7 days of the week. Missing: ${missingDates.map(fmtDate).join(', ')}`;
            }
        }

        if (forSubmit && weekStart && !canSubmitNow) {
            const msg = `This timesheet can only be submitted on or after ${fmtDate(earliestSubmitDate)}.`;
            errs['dates'] = errs['dates'] ? `${errs['dates']} ${msg}` : msg;
        }

        if (forSubmit && !declared) errs['declaration'] = 'Please confirm the declaration to submit.';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSaveDraft = async (action) => {
        if (!validate(false)) { setSnack({ open: true, msg: 'Fix validation errors first.', severity: 'error' }); return; }
        try {
            setIsLoading(true);
            const entries = [];
            for (const row of rows) {
                const existingUrls = (row.attachments || []).filter(a => a && (typeof a === 'string' ? a : a.url));
                const fileItems    = (row.attachments || []).filter(a => a && a.file);
                let finalUrls      = existingUrls;
                if (fileItems.length > 0) {
                    const fd = new FormData();
                    fileItems.forEach(f => fd.append('attachments', f.file));
                    const res      = await postData(API_ENDPOINTS.uploadTimesheetAttachments, fd);
                    const returned = (res?.result?.attachments || []).filter(a => a && a.url);
                    finalUrls = existingUrls.concat(returned);
                }
                entries.push({
                    entry_date:       row.date,
                    day_type:         row.dayType,
                    project_id:       row.project_id,
                    task_id:          row.task_id,
                    work_description: row.description,
                    hours_worked:     Number(row.hours),
                    overtime_hours:   Number(row.overtime),
                    is_billable:      row.billable,
                    location:         row.location,
                    remarks:          row.remarks,
                    attachment_urls:  finalUrls,
                    leave_type:       row.leaveType,
                });
            }

            let data     = { employee_notes: employeeComment, entries };
            let response;
            if (editData && editData.id) {
                response = await putData(API_ENDPOINTS.updateTimesheet(editData.id), data);
            } else {
                data     = { ...data, week_start_date: weekStart, week_end_date: weekEnd };
                response = await postData(API_ENDPOINTS.createTimesheet, data);
            }

            if (action !== 'submit') {
                if (response?.status === 'SUCCESS') {
                    setSnack({ open: true, msg: response?.message, severity: 'success' });
                    action === 'draft' && setTimeout(() => onDone(), 1500);
                } else {
                    setSnack({ open: true, msg: response.message || 'Save failed', severity: 'error' });
                }
            } else {
                return response?.result;
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error while updating details';
            setSnack({ open: true, msg: errorMessage, severity: 'error' });
        }
        finally{
            setIsLoading(false);
        }
    };

    const handleSubmit = async () => {
        const data = { declaration_confirmed: declared };
        try {
            const timeSheet = await handleSaveDraft('submit');
            setIsLoading(true);
            const id        = !!editData ? editData?.id : timeSheet?.id;
            const response  = await postData(API_ENDPOINTS.submitTimesheet(id), data);
            if (response?.status === 'SUCCESS') {
                setSnack({ open: true, msg: response?.message, severity: 'success' });
                setTimeout(() => onDone(), 1500);
            } else {
                setSnack({ open: true, msg: response.message || 'Save failed', severity: 'error' });
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error while updating details';
            setSnack({ open: true, msg: errorMessage, severity: 'error' });
        }
        finally{
            setIsLoading(false);
        }
    };

    const totalFiles = rows.reduce((s, r) => s + r.attachments.length, 0);

        if (isLoading) {
            return <Loader />
        }

    return (
        <Box>
            {/* ── Week header ── */}
            <Box sx={{
                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2,
                mb: 3, p: 2.5, bgcolor: '#fff', borderRadius: 3,
                border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon sx={{ fontSize: 20, color: '#6366f1' }} />
                    <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: 15 }}>Week Starting</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: {xs:'column', md:'row'}, gap: 0.5 }}>
                    <TextField type="date" size="small" value={weekStart}
                        onChange={e => handleWeekChange(e.target.value)}
                        disabled={weekCheckLoading || !!editData?.week_start_date}
                        inputProps={{ max: new Date().toISOString().split('T')[0] }}
                        sx={{
                            width: 170,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                borderColor: weekConflict ? (weekConflict.severity === 'error' ? '#fca5a5' : '#fde68a') : undefined,
                            },
                            '& .MuiInputBase-input': { fontSize: 13, py: 0.875 },
                        }}
                    />
                    {weekCheckLoading && (
                        <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>Checking existing weeks…</Typography>
                    )}
                    {weekConflict && (
                        <Alert severity={weekConflict.severity} sx={{ py: 0.25, px: 1.25, fontSize: 12, borderRadius: 1.5, '& .MuiAlert-icon': { fontSize: 16 } }}>
                            {weekConflict.msg}
                        </Alert>
                    )}
                </Box>
                {weekEnd && !weekConflict && (
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                        to <strong>{weekEnd.split('-').reverse().join('-')}</strong>
                    </Typography>
                )}
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    
                    {(() => {
                        const raw = editData ? editData.status : (selectedWeekStatus || 'DRAFT');
                        const cfg = getStatusCfg(raw) || STATUS_CFG.DRAFT;
                        return (
                            <Chip label={cfg.label} size="small"
                                sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: 12, borderRadius: 2 }} />
                        );
                    })()}
                </Box>
            </Box>

            {/* ── Rejection banner ── */}
            {editData?.status === 'REJECTED' && (
                l2RejectedRows.length > 0 ? (
                    <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>
                            The following entries were rejected by the external approver — please correct them before resubmitting:
                        </Typography>
                        {l2RejectedRows.map((row) => (
                            <Box key={row.id} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.4 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 90 }}>
                                    {row.date}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#7f1d1d' }}>
                                    {row.project || row.project_id}{row.l2_comments ? ` — ${row.l2_comments}` : ''}
                                </Typography>
                            </Box>
                        ))}
                    </Alert>
                ) : editData?.rejection_reason ? (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                            Rejection Reason
                        </Typography>
                        {editData.rejection_reason}
                    </Alert>
                ) : null
            )}

            {/* ── Entry cards (grouped by date) ── */}
            {groupedRows.map((group) => {
                const dailyWorkHours = group.date
                    ? rows.filter(r => r.date === group.date && (isWorkingRow(r) || r.dayType === 'Activity'))
                          .reduce((s, r) => s + Number(r.hours || 0), 0)
                    : 0;
                const dailyOTHours   = group.date
                    ? rows.filter(r => r.date === group.date)
                          .reduce((s, r) => s + Number(r.overtime || 0), 0)
                    : 0;
                const isOverLimit    = dailyWorkHours > maxDailyHours;
                const excessHours    = Math.max(0, dailyWorkHours - maxDailyHours);
                const isMultiEntry   = group.date && group.rows.length > 1;
                const dayColor       = DAY_COLORS[group.day] || '#64748b';

                return (
                    <Box key={group.date || group.rows[0].row.id}>
                        {isMultiEntry && (
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                px: 1.5, py: 0.75, mb: 0.5,
                                bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0',
                            }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dayColor, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: dayColor }}>
                                    {group.day} · {fmtDate(group.date)}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                                    — {group.rows.length} entries
                                </Typography>
                                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.75, alignItems: 'center' }}>
                                    {dailyWorkHours >= maxDailyHours && (
                                        <Tooltip title={`Max ${maxDailyHours} hrs reached. Any additional time must be added as Overtime.`} arrow>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#d97706', mr: 1, cursor: 'help' }}>
                                                <WarningIcon sx={{ fontSize: 16 }} />
                                                <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Max {maxDailyHours} hrs reached — log extra as OT</Typography>
                                            </Box>
                                        </Tooltip>
                                    )}
                                    {isOverLimit && (
                                        <Tooltip title={`${excessHours.toFixed(1)} hr(s) exceed the ${maxDailyHours}-hr daily limit — expand any entry to log them as Overtime`} arrow>
                                            <Chip
                                                label={`+${excessHours.toFixed(1)} hrs over limit → log as OT`}
                                                size="small"
                                                sx={{
                                                    fontSize: 10, fontWeight: 700, borderRadius: 1.5,
                                                    bgcolor: '#fef2f2', color: '#dc2626',
                                                    border: '1px solid #fca5a5', cursor: 'help',
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                    <Chip
                                        label={
                                            dailyOTHours > 0
                                                ? `${Math.min(dailyWorkHours, maxDailyHours).toFixed(1)} hrs work · ${dailyOTHours.toFixed(1)} hrs OT`
                                                : `${dailyWorkHours.toFixed(1)} hrs total`
                                        }
                                        size="small"
                                        sx={{
                                            fontSize: 11, fontWeight: 700, borderRadius: 1.5,
                                            bgcolor: isOverLimit && dailyOTHours === 0 ? '#fffbeb' : dailyOTHours > 0 ? '#eff6ff' : '#ecfdf5',
                                            color:   isOverLimit && dailyOTHours === 0 ? '#d97706' : dailyOTHours > 0 ? '#1d4ed8' : '#059669',
                                            border:  `1px solid ${isOverLimit && dailyOTHours === 0 ? '#fde68a' : dailyOTHours > 0 ? '#bfdbfe' : '#6ee7b7'}`,
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}

                        {group.rows.map(({ row, idx }, groupIdx) => {
                            const otherHoursSum = group.rows
                                .filter(({ row: r }) => r.id !== row.id && (isWorkingRow(r) || r.dayType === 'Activity'))
                                .reduce((sum, { row: r }) => sum + Number(r.hours || 0), 0);
                            const rowMaxDailyHours = Math.max(0, maxDailyHours - otherHoursSum);
                            const firstRow = group.rows[0]?.row;

                            return (
                                <Box key={row.id} sx={isMultiEntry ? { pl: 2 } : {}}>
                                    <EntryRow
                                        row={row} rowIndex={idx} projects={projects}
                                        errors={errors} weekStart={weekStart} weekEnd={weekEnd}
                                        onUpdate={updateRow} onReplace={replaceRow} onDelete={deleteRow} isOnly={rows.length === 1}
                                        onSaveDraft={() => handleSaveDraft()}
                                        onAddForDate={addRowForDate}
                                        userId={userId}
                                        isFirstForDate={groupIdx === 0}
                                        leaveTypes={leaveTypes}
                                        maxDailyHours={rowMaxDailyHours}
                                        isMultiEntry={isMultiEntry}
                                        firstRowDayType={firstRow?.dayType}
                                        firstRowLeaveType={firstRow?.leaveType}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                );
            })}

            <Tooltip title={allWeekDatesFilled ? 'All 7 days of this week already have entries' : ''}>
                <span>
                    <Button startIcon={<AddRowIcon />} variant="outlined" onClick={addRow}
                        disabled={allWeekDatesFilled}
                        sx={{ textTransform: 'none', borderRadius: 2, color: '#6366f1', borderColor: '#c7d2fe', fontWeight: 600, mb: 3, mt: 0.5, '&:hover': { bgcolor: '#eef2ff', borderColor: '#6366f1' }, '&.Mui-disabled': { color: '#94a3b8', borderColor: '#e2e8f0' } }}>
                        Add Entry
                    </Button>
                </span>
            </Tooltip>

            {/* ── Weekly summary ── */}
            <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, overflow: 'hidden', mb: 3, bgcolor: '#fff' }}>
                <Box sx={{ bgcolor: '#f8fafc', px: 2.5, py: 1.25, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#475569' }}>Weekly Summary</Typography>
                    <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>Auto-calculated from all entries</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                    {[
                        { label: 'Regular Hours',  value: summary.total,       fmt: v => v.toFixed(2), unit: 'hrs',  color: '#6366f1', bg: '#eef2ff' },
                        { label: 'Overtime Hours', value: summary.ot,          fmt: v => v.toFixed(2), unit: 'hrs',  color: '#f59e0b', bg: '#fffbeb' },
                        { label: 'Leave Days',     value: summary.leaves,      fmt: v => v,            unit: 'days', color: '#7c3aed', bg: '#f5f3ff' },
                        { label: 'Holidays',       value: summary.holidays,    fmt: v => v,            unit: 'days', color: '#d97706', bg: '#fffbeb' },
                        { label: 'Weekend Days',   value: summary.weekends,    fmt: v => v,            unit: 'days', color: '#64748b', bg: '#f1f5f9' },
                    ].map((item, i) => (
                        <Box key={item.label} sx={{ flex: '1 1 110px', px: 2, py: 1.5, borderRight: i < 4 ? '1px solid #e2e8f0' : 'none', bgcolor: item.bg }}>
                            <Typography sx={{ fontSize: 10, color: '#64748b', mb: 0.25 }}>{item.label}</Typography>
                            <Typography sx={{ fontSize: 20, fontWeight: 700, color: item.color, lineHeight: 1.1 }}>
                                {item.fmt(item.value)}
                                <Typography component="span" sx={{ fontSize: 11, fontWeight: 400, ml: 0.4, color: '#94a3b8' }}>{item.unit}</Typography>
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* ── Comments ── */}
            <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#475569', mb: 1 }}>
                    Comments for Approver{' '}
                    <Typography component="span" sx={{ fontSize: 12, color: '#94a3b8', fontWeight: 400 }}>(optional)</Typography>
                </Typography>
                <TextField fullWidth multiline rows={3} placeholder="Any additional notes for your manager…"
                    value={employeeComment} onChange={e => setEmployeeComment(e.target.value)}
                    inputProps={{ maxLength: 500 }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, bgcolor: '#fff' }}
                />
            </Box>

            <Divider sx={{ mb: 2.5 }} />

            {/* ── Declaration ── */}
            <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: errors.declaration ? '#fef2f2' : '#f8fafc', border: `1px solid ${errors.declaration ? '#fca5a5' : '#e2e8f0'}` }}>
                <FormControlLabel
                    control={
                        <Checkbox checked={declared}
                            onChange={e => { setDeclared(e.target.checked); setErrors(p => ({ ...p, declaration: undefined })); }}
                            sx={{ color: errors.declaration ? '#d32f2f' : '#6366f1', '&.Mui-checked': { color: '#6366f1' } }}
                        />
                    }
                    label={
                        <Typography variant="body2" sx={{ color: errors.declaration ? '#d32f2f' : '#475569', fontWeight: 500 }}>
                            I confirm that the above timesheet entries are accurate and complete to the best of my knowledge.
                        </Typography>
                    }
                />
                {errors.declaration && (
                    <Typography sx={{ fontSize: 12, color: '#d32f2f', ml: 4 }}>{errors.declaration}</Typography>
                )}
            </Box>

            {errors.dates && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                    {errors.dates}
                </Alert>
            )}

            {/* ── Actions ── */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <Button variant="outlined" startIcon={<SaveIcon />} onClick={() => handleSaveDraft('draft')}
                    disabled={!!weekConflict}
                    sx={{ textTransform: 'none', borderRadius: 2, color: '#64748b', borderColor: '#cbd5e1', fontWeight: 600, px: 3, '&:hover': { bgcolor: '#f8fafc' } }}>
                    Save as Draft
                </Button>
                <Tooltip title={!canSubmitNow ? `You can submit this timesheet on or after ${fmtDate(earliestSubmitDate)}, once the 5th working day of the week has started.` : ''}>
                    <span>
                        <Button variant="contained" startIcon={<SendIcon />}
                            onClick={() => { if (validate(true)) setSubmitDialogOpen(true); }}
                            disabled={!declared || !canSubmitNow}
                            sx={{ textTransform: 'none', borderRadius: 2, bgcolor: '#6366f1', fontWeight: 600, px: 3, '&:hover': { bgcolor: '#4338ca' }, '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' } }}>
                            Submit for Approval
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnack(p => ({ ...p, open: false }))} severity={snack.severity} sx={{ borderRadius: 2 }}>{snack.msg}</Alert>
            </Snackbar>

            {/* ── Submit confirmation dialog ── */}
            <SubmitConfirmDialog
                open={submitDialogOpen}
                onClose={() => setSubmitDialogOpen(false)}
                onConfirm={() => { setSubmitDialogOpen(false); handleSubmit(); }}
                rows={rows}
                projects={projects}
                summary={summary}
                weekStart={weekStart}
                weekEnd={weekEnd}
                reportingManagerName={reportingManagerName}
                globalConfig={globalConfig}
            />
        </Box>
    );
}

// ─── Submit confirmation dialog ───────────────────────────────────────────────
function getInitials(name) {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function ApproverAvatar({ name, color }) {
    return (
        <Box sx={{
            width: 28, height: 28, borderRadius: '50%',
            bgcolor: color, display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
        }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#fff' }}>{getInitials(name)}</Typography>
        </Box>
    );
}

function SubmitConfirmDialog({ open, onClose, onConfirm, rows, projects, summary, weekStart, weekEnd, reportingManagerName, globalConfig }) {
    const workingRows = rows.filter(r => isWorkingRow(r) && r.project_id);

    const routing = useMemo(() => {
        const map = {};
        workingRows.forEach(r => {
            if (!map[r.project_id]) {
                const projectObj = projects.find(p => p.id === r.project_id);
                map[r.project_id] = {
                    projectName:          r.project || 'Unknown Project',
                    externalApproverName: projectObj?.external_approver_name || null,
                    entryCount:           0,
                };
            }
            map[r.project_id].entryCount += 1;
        });
        return Object.values(map);
    }, [workingRows, projects]);

    const fmtWeek = (s) => s ? new Date(s + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

    const totalEntries = rows.filter(r => isWorkingRow(r) || r.dayType === 'Activity').length;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' } }}>

            {/* Header */}
            <DialogTitle sx={{ pb: 1.5, pt: 2.5, px: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <SendIcon sx={{ fontSize: 18, color: '#6366f1' }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>Submit for Approval</Typography>
                        <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                            Week of {fmtWeek(weekStart)} — {fmtWeek(weekEnd)}
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ px: 3, pb: 0 }}>
                <Divider sx={{ mb: 2 }} />

                {/* Summary pills */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
                    <Chip label={`${totalEntries} entr${totalEntries !== 1 ? 'ies' : 'y'}`} size="small"
                        sx={{ bgcolor: '#eef2ff', color: '#4338ca', fontWeight: 600, fontSize: 11 }} />
                    <Chip label={`${summary.total.toFixed(1)} hrs`} size="small"
                        sx={{ bgcolor: '#f0fdf4', color: '#15803d', fontWeight: 600, fontSize: 11 }} />
                    {summary.ot > 0 && (
                        <Chip label={`${summary.ot.toFixed(1)} OT hrs`} size="small"
                            sx={{ bgcolor: '#fffbeb', color: '#b45309', fontWeight: 600, fontSize: 11 }} />
                    )}
                    {summary.leaves > 0 && (
                        <Chip label={`${summary.leaves} leave day${summary.leaves !== 1 ? 's' : ''}`} size="small"
                            sx={{ bgcolor: '#f5f3ff', color: '#7c3aed', fontWeight: 600, fontSize: 11 }} />
                    )}
                    {summary.weekends > 0 && (
                        <Chip label={`${summary.weekends} weekend day${summary.weekends !== 1 ? 's' : ''}`} size="small"
                            sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: 11 }} />
                    )}
                </Box>

                {/* Routing section */}
                {routing.length > 0 && (
                    <>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.25 }}>
                            Approval Routing
                        </Typography>
                        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                            {routing.map((item, idx) => (
                                <Box key={idx} sx={{
                                    display: 'flex', alignItems: 'flex-start', gap: 2,
                                    px: 2, py: 1.5,
                                    borderBottom: idx < routing.length - 1 ? '1px solid #f1f5f9' : 'none',
                                    bgcolor: idx % 2 === 0 ? '#fff' : '#fafafa',
                                }}>
                                    {/* Project name + entry count */}
                                    <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {item.projectName}
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.25 }}>
                                            {item.entryCount} entr{item.entryCount !== 1 ? 'ies' : 'y'}
                                        </Typography>
                                    </Box>

                                    {/* Approvers */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-end' }}>
                                        {/* Internal approver */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <ApproverAvatar name={reportingManagerName} color="#6366f1" />
                                            <Box>
                                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1e293b', lineHeight: 1.2 }}>
                                                    {reportingManagerName || 'Not assigned'}
                                                </Typography>
                                                <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>Internal · Manager</Typography>
                                            </Box>
                                        </Box>

                                        {/* External approver — only shown when two-level approval is globally enabled */}
                                        {globalConfig?.is_two_level_approval && (() => {
                                            const isCompany = globalConfig.approval_scope === 'COMPANY';
                                            const approverName = isCompany
                                                ? (globalConfig.external_approver_name || null)
                                                : (item.externalApproverName || null);
                                            return approverName ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <ApproverAvatar name={approverName} color="#0891b2" />
                                                    <Box>
                                                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1e293b', lineHeight: 1.2 }}>
                                                            {approverName}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>External · Approver</Typography>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <WarningIcon sx={{ fontSize: 13, color: '#f59e0b' }} />
                                                    <Typography sx={{ fontSize: 11, color: '#b45309' }}>
                                                        {isCompany ? 'No org-level external approver set' : 'No external approver for this project'}
                                                    </Typography>
                                                </Box>
                                            );
                                        })()}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </>
                )}

                <Typography sx={{ fontSize: 12, color: '#64748b', pb: 2 }}>
                    Once submitted, your timesheet cannot be edited until it is reviewed.
                </Typography>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button onClick={onClose} variant="outlined"
                    sx={{ textTransform: 'none', borderRadius: 2, color: '#64748b', borderColor: '#cbd5e1', fontWeight: 600, px: 3, '&:hover': { bgcolor: '#f8fafc' } }}>
                    Cancel
                </Button>
                <Button onClick={onConfirm} variant="contained" startIcon={<SendIcon />}
                    sx={{ textTransform: 'none', borderRadius: 2, bgcolor: '#6366f1', fontWeight: 600, px: 3, '&:hover': { bgcolor: '#4338ca' } }}>
                    Confirm Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default TimesheetForm;
